import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../lib/api.js'
import { rollupState, pickedRepLine } from '../../lib/bowlCoverage.js'
import DeskPicker from './DeskPicker.jsx'
import styles from './repVolume.module.css'

/**
 * BOWL DESKS BY CONFERENCE, AND THE PICKS THAT SET THEM.
 *
 * Brian, 2026-10-26: "I noticed the AAC still doesn't show as assigned in bowl
 * even though I've already discussed exactly who's going after those
 * accounts." So a conference's status is computed by the backend FROM ITS
 * SCHOOLS (`conferenceDesks`): assigned when every school has one accountable
 * desk, split when some sit on a shared conference nobody has picked for,
 * partial when some have no desk. The line under each status says how each
 * school got its desk, so "assigned" never hides that some came from the
 * conference breakdown rather than a school decision.
 *
 * Rendered on the Bowl Eligibility board AND in the Command Center's Bowl
 * Season space. In the Command Center it reads the board itself and links to
 * the board for per-school picks; the conference-level pick works in both.
 *
 * `rows` (optional) is the board's `conferenceDesks`. When absent the panel
 * fetches the board, so the Command Center does not need to know its shape.
 */
export default function BowlDeskPanel({ rows = null, canPick = false, expectedVersion = null, season = null, onPicked = null, inCommandCenter = false }) {
  const [board, setBoard] = useState({ status: rows ? 'ready' : 'loading', rows: rows || [], error: '' })
  const [picks, setPicks] = useState({ status: 'loading', data: null, error: '' })

  const [reload, setReload] = useState(0)
  useEffect(() => {
    const ctl = new AbortController()
    const update = (setter, value) => { if (!ctl.signal.aborted) setter(value) }
    setBoard({status: rows ? 'ready' : 'loading', rows: rows || [], error: ''})
    setPicks({status: 'loading', data: null, error: ''})
    if (!rows) {
      api.fbsBowlEligibility({signal: ctl.signal})
        .then(d => update(setBoard, {status: 'ready', rows: d?.conferenceDesks || [], error: '', version: d?.deskMeta?.version, season: d?.snapshot?.season, canPick: d?.canPick}))
        .catch(e => update(setBoard, {status: 'error', rows: [], error: e?.body?.detail || e?.message || 'Could not read the board.'}))
    }
    api.fbsBowlDeskPicks({signal: ctl.signal})
      .then(d => update(setPicks, {status: 'ready', data: d, error: ''}))
      .catch(e => update(setPicks, {status: 'error', data: null, error: e?.body?.detail || e?.message || 'Could not read the picks.'}))
    return () => ctl.abort()
  }, [rows, reload])

  const picked = r => { setReload(n => n + 1); onPicked?.(r) }
  const list = rows || board.rows || []
  const version = rows ? expectedVersion : board.version
  const displayedSeason = rows ? season : board.season
  const mayPick = canPick && picks.status === 'ready' && picks.data?.canPick === true
    && (rows || board.canPick === true) && Number.isSafeInteger(version)
    && picks.data?.deskMeta?.version === version && Number.isInteger(displayedSeason)
    && (picks.data?.season ?? picks.data?.deskMeta?.season) === displayedSeason

  return (
    <div>
      <h2>Bowl desks by conference</h2>
      {board.status === 'loading' && <p className={styles.deskWhy}>Reading the board…</p>}
      {board.status === 'error' && (
        <p className={styles.deskBad}>The board could not be read, so no conference status is shown. {board.error}</p>
      )}
      {board.status === 'ready' && list.length === 0 && (
        <p className={styles.deskWhy}>The board carried no conference rollup yet.</p>
      )}
      {list.length > 0 && (
        <ul className={styles.pickList}>
          {list.map((c) => {
            const st = rollupState(c.status, c)
            return (
              <li key={c.conference} className={styles.pickRow}>
                <b>{c.conference}</b>{' '}
                <span className={`${styles.rollupPill} ${styles[`rollup_${st.tone}`]}`} title={st.why}>{st.label}</span>
                <span className={styles.deskWhy}>{c.line}</span>
                {c.needsPickTeams?.length > 0 && (
                  <span className={styles.deskWhy}>Needs a pick: {c.needsPickTeams.join(', ')}</span>
                )}
                {c.noDeskTeams?.length > 0 && (
                  <span className={styles.deskBad}>No desk: {c.noDeskTeams.join(', ')}</span>
                )}
                {mayPick && c.split && (
                  <DeskPicker conference={c.conference} candidates={c.candidates} expectedVersion={version} season={displayedSeason} onPicked={picked} />
                )}
              </li>
            )
          })}
        </ul>
      )}

      <h3>Recent desk picks</h3>
      <button className="btn" type="button" onClick={() => { setReload(n => n + 1); onPicked?.() }}>Read current desks and picks</button>
      {picks.status === 'loading' && <p className={styles.deskWhy}>Reading recent desk picks...</p>}
      {picks.status === 'error' && <p className={styles.deskBad}>{picks.error}</p>}
      {picks.status === 'ready' && (picks.data?.picks || []).length === 0 && (
        <p className={styles.deskWhy}>
          No picks {picks.data?.scope === 'own' ? 'on your desk ' : ''}yet. The request succeeded and the answer is none.
        </p>
      )}
      {(picks.data?.picks || []).length > 0 && (
        <ul className={styles.pickList}>
          {picks.data.picks.slice(0, 12).map((p) => (
            <li key={p.id} className={styles.pickRow}>
              <b>{p.target}</b>
              {p.action === 'clear'
                ? ' back to its default desk'
                : ` to ${p.kind === 'school-team' ? (p.members || []).map(m => m.name).join(' + ') : pickedRepLine(p.reps)}`}
              <span className={styles.deskWhy}>
                {p.actorName || p.actorId}, {new Date(p.at).toLocaleString()}: {p.reason}
              </span>
            </li>
          ))}
        </ul>
      )}
      {picks.data?.note && <p className={styles.deskWhy}>{picks.data.note}</p>}
      {inCommandCenter && (
        <Link className={styles.deskAction} to="/bowl-eligibility">Open the bowl board to pick per school</Link>
      )}
    </div>
  )
}
